import {initializeApp} from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth, onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updateProfile,
  updateEmail,
  signOut
} from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  updateDoc,
  where,
  collectionGroup
} from "firebase/firestore";
import { getAnalytics } from "firebase/analytics";
import {Game, Pitch, Player, TeamInfo, ThrowHand, UserInfo} from "../../models/utilTypes";
import {OpenLevel} from "../../models/fixture/openLevel";
import {UserRole} from "../../models/fixture/userRole";
import {Position} from "../../models/position";
import {IMainDb} from "./mainDb";

const firebaseApp = initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
  authDomain: process.env.REACT_APP_FIREBASE_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
});

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);
const analytics = getAnalytics(firebaseApp);

export class Firebase implements IMainDb {

  private static _instance: Firebase;

  private constructor() {
  }

  public static get instance() {
    if(!this._instance){
      this._instance = new Firebase();
    }
    return this._instance;
  }

  async signIn(email:string, password: string) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user.uid;
  }

  async signUp(email:string, password:string, username: string) {
    const authUser = await createUserWithEmailAndPassword(auth, email, password);
    if (authUser) {
      // Firebaseのauthentication情報を更新する
      await updateProfile(authUser.user, {displayName: username});
      const userInfo: UserInfo = {
        id: authUser.user.uid,
        name: username,
        email: email,
        myTeam: '',
        myTeamId: '',
        myTeamRegion: 0,
        role: UserRole.general,
      }
      await setDoc(doc(db, 'users', authUser.user.uid), userInfo);
      return userInfo;
    } else {
      throw new Error('新規登録に失敗しました。\nネット環境を確認して、もう一度登録をお願いします。')
    }
  }

  async signOut(): Promise<void> {
    await signOut(auth);
  }

  isLogin(dispatch:any){
    onAuthStateChanged(auth,(user) => {
      if (user) {
        // User is signed in.
        dispatch({
          type: "login",
          payload: {
            user:{
              id: user.uid,
              name: user.displayName!,
              email: user.email!,
              myTeamId: '',
              role: 0,
              myTeam: '',
              myTeamRegion: 0
            },
          },
        });
      } else {
        // User is signed out.
        dispatch({
          type: "logout",
        });
      }
    });
  }

  async resetEmail(email: string) {
    await sendPasswordResetEmail(auth, email);
  }

  async getUser(uid:string) {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    if(userSnap.exists()){
      const userDbInfo: UserInfo = {
        id: uid,
        name: userSnap.data()?.name,
        email: userSnap.data()?.email,
        myTeamId: userSnap.data()?.myTeamId,
        role: userSnap.data()?.role
      }
      // 画面側に持たせる情報
      const userInfoNotJoined : UserInfo ={
        id: userDbInfo.id,
        name: userDbInfo.name,
        email: userDbInfo.email,
        myTeamId: '',
        myTeam: '',
        myTeamRegion: 0,
        role: UserRole.general,
      }
      if (userDbInfo.myTeamId){
        const teamRef = doc(db, 'teams', userDbInfo.myTeamId);
        const teamSnapshot = await getDoc(teamRef)
        if(teamSnapshot.exists()) {
          const userInfo: UserInfo = {
            id: userDbInfo.id,
            name: userDbInfo.name,
            email: userDbInfo.email,
            myTeamId: userDbInfo.myTeamId,
            myTeam: teamSnapshot.data()?.myTeam,
            myTeamRegion: teamSnapshot.data()?.myTeamRegion,
            role: userDbInfo.role
          }
          return userInfo;
        } else {
          // もし所属チームデータが消されていた時
          const userRef = doc(db, 'users', userDbInfo.id);
          const updateData = {
            myTeamId: '',
            role: UserRole.general
          }
          await updateDoc(userRef, updateData);
          return userInfoNotJoined;
        }
      } else {
        return userInfoNotJoined;
      }
    } else {
      throw new Error('ユーザーが見つかりません');
    }
  }

  async updateUser(userId:string, name: string, email: string) {
    const ref = doc(db, 'users', userId);
    await updateDoc(ref, {name: name});
    if (email) {
      await updateEmail(auth.currentUser!, email);
    }
  }

  async updateUserMyTeam(userId:string, teamId:string, role:UserRole) {
    const userDocRef = doc(db, 'users', userId);
    const myTeamInfo = {
      myTeamId: teamId,
      role: role,
    }
    await updateDoc(userDocRef, myTeamInfo);
  }

  async deleteUser(userId: string) {
    const ref = doc(db, 'users', userId);
    await deleteDoc(ref);
  }

  async createTeam(teamName: string, joinPass:string, regionNum:number, openLevel: OpenLevel, newTeamId:string ) {
    const docData: TeamInfo = {
      id: newTeamId,
      name: teamName,
      password: joinPass,
      region: regionNum,
      openLevel: openLevel,
    }
    const teamDocRef = doc(db, 'teams', newTeamId);
    await setDoc(teamDocRef, docData);
  }

  async checkMatchedTeam(teamName:string, teamRegion:number) {
    const teamRef = collection(db, 'teams');
    const q = query(
      teamRef,
      where('name', '==', teamName),
      where('region', '==', teamRegion)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty && querySnapshot.size === 1) {
      const teams: TeamInfo[] = [];
      querySnapshot.forEach(v => {
        teams.push(v.data() as TeamInfo);
      });
      return teams[0];
    } else if (!querySnapshot.empty && querySnapshot.size > 1) {
      throw new Error('同じ地域で同じ名前のチームが存在します');
    } else {
      return undefined;
    }
  }
  
  async getTeamInfo(teamId: string) {
    const teamRef = doc(db, 'teams', teamId);
    const teamSnap = await getDoc(teamRef);
    const data = teamSnap.data() as TeamInfo
    if (!data) {
      throw new Error('チーム情報を取得できませんでした');
    }
    return {
      id: data.id,
      name: data.name,
      region: data.region,
      password: data.password,
      openLevel: data.openLevel
    }
  }

  async deleteTeam(teamId:string){
    const teamRef = doc(db, 'teams', teamId);
    await deleteDoc(teamRef);
  }

  async updateTeam(teamId:string, teamName:string, teamPassword:string, teamRegion:number, openLevel: OpenLevel ) {
    const teamRef = doc(db, 'teams', teamId);
    const teamNewData = {
      name: teamName, password: teamPassword, region:teamRegion, openLevel: openLevel
    }
    await updateDoc(teamRef, teamNewData);
  }
  
  async addPlayer(playerId:string, name:string, birth:string, position:Position[], throwHand:ThrowHand, teamId:string) {
    const playerRef = doc(db, 'teams', teamId, 'players', playerId);
    const playerData: Player = {
      id: playerId,
      name: name,
      birth: birth,
      throwHand: throwHand,
      position: position,
    }
    await setDoc(playerRef, playerData);
    return playerData;
  }

  async updatePlayer(playerId: string, name:string, birth:string, position:Position[], throwHand:ThrowHand, myTeamId:string):Promise<void>{
    const playerRef = doc(db, 'teams', myTeamId, 'players', playerId)
    const newPlayerInfo = {
      name: name,
      position: position,
      birth: birth,
      throwHand: throwHand,
    }
    await updateDoc(playerRef, newPlayerInfo);
  }

  async deletePlayer(playerId: string, myTeamId:string):Promise<void>{
    const playerRef = doc(db, 'teams', myTeamId, 'players', playerId);
    await deleteDoc(playerRef);
  }
  
  async addGame(gameId:string, vsTeam: string, dateTime: string, pitches: Pitch[], teamId:string): Promise<Game> {
    const gameRef = doc(db, 'teams', teamId, 'games', gameId);
    const gameRef2 = collection(db, 'teams', teamId, 'games');
    const q = query(gameRef2, where('dateTime' ,'==', dateTime));
    const snap = await getDocs(q);
    if(snap.empty){
      const gameData: Game = {
        id: gameId,
        vsTeam: vsTeam,
        dateTime: dateTime,
        pitches: pitches,
      };
      await setDoc(gameRef, gameData);
      return gameData;
    } else {
      throw new Error('同じ日時の登録試合があります。\n投手の追加は登録データ編集から行ってください')
    }
  }

  async updateGame(gameId:string, vsTeam: string, dateTime: string, pitches: Pitch[], myTeamId:string): Promise<void>{
    const gameRef = doc(db, 'teams', myTeamId, 'games', gameId);
    const gameData = {
      vsTeam: vsTeam,
      dateTime: dateTime,
      pitches: pitches
    }
    await updateDoc(gameRef, gameData);
  }

  async deleteGame(gameId: string, myTeamId:string): Promise<void>{
    const gameRef = doc(db, 'teams', myTeamId, 'games', gameId);
    await deleteDoc(gameRef);
  }
  
  async getTeamGames(teamId: string): Promise<Game[]> {
    const gameRef = collection(db, 'teams', teamId, 'games');
    const gameDate = await getDocs(gameRef);
    if (!gameDate.empty) {
      return gameDate.docs.map(v => v.data() as Game)
    } else {
      return [];
    }
  }
  
  async getTeamPlayers(teamId: string): Promise<Player[]> {
    const playersRef = collection(db, 'teams', teamId, 'players');
    const gameDate = await getDocs(playersRef);
    if (!gameDate.empty) {
      return gameDate.docs.map(v => v.data() as Player)
    } else {
      return [];
    }
  }
  
  async getAll(): Promise<void>{
    const allGames = query(collection(db, 'teams'), where('id', '==', true));
    const snap = await getDocs(allGames);
    snap.forEach((v) => {
      console.log(v.data());
    });
  }
}
